import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { videoCategories, hotVideos } from '@/mocks/videos';
import { faqList } from '@/mocks/brand';

function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20stylized%20abstract%20cinematic%20composition%20with%20warm%20coral%20and%20amber%20gradients%2C%20flowing%20organic%20shapes%20reminiscent%20of%20film%20strips%20and%20light%20beams%2C%20dark%20elegant%20background%20with%20soft%20glowing%20accents%2C%20modern%20minimalist%20art%20style%2C%20luxurious%20texture%2C%20no%20text&width=1800&height=900&seq=douhua-hero-bg&orientation=landscape"
        alt="豆花视频 - 高清视频内容平台"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 tracking-tight">
          {t('home.hero.title')}
        </h1>
        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
          {t('home.hero.subtitle')}
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/category/all"
            className="px-6 md:px-8 py-3 md:py-3.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line mr-2"></i>
            {t('home.hero.cta')}
          </Link>
          <Link
            to="/about"
            className="px-6 md:px-8 py-3 md:py-3.5 bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-full text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            {t('home.hero.secondary')}
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoriesSection() {
  const { t } = useTranslation();

  return (
    <section className="w-full py-12 md:py-16 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
            {t('home.categories.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500">
            {t('home.categories.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {videoCategories.filter(c => c.slug !== 'all').map((category) => (
            <Link
              key={category.slug}
              to={`/category/${category.slug}`}
              className="group flex flex-col items-center gap-3 p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                <i className={`${category.icon} text-xl md:text-2xl`}></i>
              </div>
              <span className="text-sm md:text-base font-medium text-foreground-700 group-hover:text-primary-600 transition-colors duration-300 whitespace-nowrap">
                {category.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotContentSection() {
  const { t } = useTranslation();

  return (
    <section className="w-full py-12 md:py-16 bg-background-100">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {t('home.hot.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-500">
              {t('home.hot.subtitle')}
            </p>
          </div>
          <Link
            to="/category/all"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('home.hot.viewMore')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {hotVideos.map((video) => (
            <Link
              key={video.id}
              to={`/article/${video.id}`}
              className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative overflow-hidden aspect-video">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                  {video.duration}
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-play-fill text-2xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-3 md:p-4">
                <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-2 mb-2 leading-relaxed">
                  {video.description}
                </p>
                <div className="flex items-center justify-between text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {video.rating}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center sm:hidden">
          <Link
            to="/category/all"
            className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('home.hot.viewMore')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation();

  return (
    <section className="w-full py-12 md:py-16 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
            {t('home.faq.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500">
            {t('home.faq.subtitle')}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqList.map((faq, index) => (
            <details key={index} className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                  {faq.question}
                </span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:hidden">
                  <i className="ri-add-line"></i>
                </div>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 hidden group-open:flex">
                  <i className="ri-subtract-line"></i>
                </div>
              </summary>
              <div className="px-4 md:px-5 pb-4 md:pb-5">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '豆花视频',
    url: 'https://www.douhuavideo.com/',
    description: '豆花视频是您发现、观看与分享优质视频内容的首选平台。海量高清影视、短视频、生活娱乐内容一网打尽。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.douhuavideo.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '豆花视频',
    url: 'https://www.douhuavideo.com',
    description: '豆花视频是您发现、观看与分享优质视频内容的首选平台。海量高清影视、短视频、生活娱乐内容一网打尽。',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg type="WebSite" data={webPageSchema} />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoriesSection />
          <HotContentSection />
          <FAQSection />
        </main>
        <Footer />
      </div>
    </>
  );
}